import React, {useEffect, useState} from "react";
import {statusOptionen} from "../../../consts/valueLists";
import {userHasAnyRole} from "../../../utils/SecurityUtils";
import {FACHADMIN, SYSTEMADMIN, ZUST_SBD} from "../../../consts/funktionaleRollen";
import {LageVerortung} from "../common/LageVerortung";
import EAFieldset from "../../EAFieldset";
import {useHttp} from "../../../services/HttpService";
import {
    getMessstelleAbwasserartEndpoint,
    getMessstelleAnhangEndpoint,
    getMessstellenZustaendigkeitEndpoint
} from "../../../services/WertelistenService";
import {useHttpSortById} from "../../../utils/usePreformattedHttp";
import EADialog from "../../EADialog";
import EATextField from "../../EATextField";
import EAButton from "../../EAButton";
import EATable from "../../EATable";
import {createColumn, createComponentColumn} from "../../../utils/ColumnUtils";
import {Grid} from "@material-ui/core";
import EATableActionButton from "../../tableComponents/EATableActionButton";
import EAButtonBar from "../../EAButtonBar";
import * as PropTypes from "prop-types";
import {getMessstelleAnlagenEndpoint} from "../../../services/AnlagenService";

const id = "messstellendaten";

export function MessstelleZuordnenTableDialog(props) {
    let [{data, loading}] = useHttpSortById({
        url: getMessstelleAnlagenEndpoint(props.messstellenId),
        timeout: 600000,
    });
    const [filteredData, setFilteredData] = useState([]);
    const [filter, setFilter] = useState("");

    useEffect(() => {
        if (data) {
            setFilteredData([
                ...data.filter((item) => {
                    return (
                        item.id !== props.anlageId &&
                        (!filter ||
                            item.betrieb.value.toLowerCase().includes(filter.toLowerCase()) ||
                            item.bezeichnung.toLowerCase().includes(filter.toLowerCase()))
                    );
                }),
            ]);
        }
    }, [data, props.anlageId, filter, loading]);

    return (
        <EADialog
            open={props.open}
            titleChildren={"Übergeordnete Anlage auswählen"}
            content={
                <div style={{width: "700px", maxHeight: "520px", minHeight: "400px"}}>
                    <div className="gridl" style={{gridTemplateColumns: "repeat(3,auto)", paddingBottom: "8px"}}>
                        <EATextField
                            value={filter}
                            onChange={(e) => {
                                e.preventDefault();
                                setFilter(e.target.value);
                            }}
                            id="parentAnlageFilter"
                            data-cy="parentAnlageFilter"
                            label="Suchkriterium"
                        />
                        <div>
                            <EAButton
                                onClick={() => {
                                    setFilter("");
                                }}
                                id="parentAnlageFilterSucheReset"
                                data-cy="parentAnlageFilterSucheReset"
                                label="Zurücksetzen"
                            />
                        </div>
                    </div>
                    <EATable
                        id="parentAnlageAuswahl"
                        initialSortColumn="mipNr"
                        columns={[
                            createComponentColumn(
                                "actions",
                                (row, index) => {
                                    return (
                                        <Grid container spacing={1} justify="center">
                                            <Grid item>
                                                <EATableActionButton id={"selectAufnahme-" + index} iconName="ico-check">
                                                    <span
                                                        onClick={() => {
                                                            setFilter("");
                                                            props.onClickOk(row);
                                                        }}
                                                        className={"w-100"}
                                                        style={{
                                                            height: "100%",
                                                            display: "block",
                                                        }}
                                                    />
                                                </EATableActionButton>
                                            </Grid>
                                        </Grid>
                                    );
                                },
                                "",
                                null,
                                {width: "50px", padding: "0"}
                            ),
                            createColumn("bezeichnung", "Abwasseranlage"),
                            createColumn("anlagentyp.value", "Anlagentyp")
                        ]}
                        data={filteredData}
                        loading={loading}
                    />
                </div>
            }
            buttons={
                <EAButtonBar>
                    <EAButton id={"btnAbbrechen"} data-cy={"btnAbbrechen"} label={"Abbrechen"} onClick={props.onClickCancel}/>
                </EAButtonBar>
            }
            closeFunction={props.onClickCancel}
        />
    );
}

MessstelleZuordnenTableDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClickCancel: PropTypes.func.isRequired,
    onClickOk: PropTypes.func.isRequired,
};

function MessstellenDaten({formFactory, data, onChange, statusDisabled, zustaendigeBehoerdenDisabled, modus}) {
    const [abwasserArtOptionen] = useHttpSortById(getMessstelleAbwasserartEndpoint);
    const [anhangOptionen] = useHttp(getMessstelleAnhangEndpoint);
    const [{
        data: zustaendigkeitenOptionen,
        loading: zustaendigkeitenOptionenLoading
    }] = useHttp(getMessstellenZustaendigkeitEndpoint);
    const [transformedAnhangOptionen, setTransformedAnhangOptionen] = useState({})

    const disabled = !userHasAnyRole([ZUST_SBD, FACHADMIN, SYSTEMADMIN]);

    useEffect(() => {
        if (anhangOptionen.data) {
            const transformedData = anhangOptionen.data.map(option => ({
                ...option,
                value: option.bezeichnung,
            }));

            setTransformedAnhangOptionen({
                ...anhangOptionen,
                data: transformedData,
            });
        }
    }, [anhangOptionen]);

    return (
        <div className="gridl tabsheet-content-margin">
            <div className="gridl" style={{gridTemplateColumns: "130px 1fr 150px 130px 130px"}}>
                {formFactory.renderInput("messstellenId", id + "-messstellenId", "Messstellen-Nr.", "text", true)}
                {formFactory.renderInput("bezeichnung", id + "-bezeichnung", "Bezeichnung", "text", disabled)}
                {formFactory.renderInput("messstellentyp.value", id + "-typ", "Messstellentyp", "text", true)}
                {formFactory.renderError("messstellentyp", id + "-typ-error")}
                {formFactory.renderSelect("messstellenstatus", id + "-status", "Status", statusOptionen, disabled || statusDisabled, {
                    optionTitle: "value",
                    optionKey: "id",
                    style: {gridColumn: 4},
                })}
                {formFactory.renderDatePicker("statuswechsel",
                    "statuswechsel",
                    "Seit",
                    disabled || statusDisabled,
                )}
            </div>
            <div className="gridl" style={{paddingTop: "var(--vertical-spacing)", gridTemplateColumns: "repeat(3, max-content) 1fr"}}>
                {formFactory.renderCheckbox("abgabeRelevant", id + "-abgaberelevant", "Abgaberelevant", disabled)}
                {formFactory.renderCheckbox("frachtRelevant", id + "-frachtrelevant", "Frachtrelevant", disabled)}
                {formFactory.renderCheckbox("waermeRelevant", id + "-waermerelevant", "Wärmerelevant", disabled)}
            </div>
            <LageVerortung
                formFactory={formFactory}
                disabled={disabled}
                data={data}
                onClick={(lage) => onChange("lage", lage)}
                isButtonVisible={!disabled}
                modus={modus}
            />
            <EAFieldset caption="Anhang">
                <div className="gridl" style={{gridTemplateColumns: "1fr 155px"}}>
                    {formFactory.renderAutocompleteWerteliste(
                        "anhang.anhangAbwasserverordnung",
                        id + "-anhang",
                        "Anhang AbwV",
                        transformedAnhangOptionen,
                        false,
                        disabled
                    )}
                    {formFactory.renderAutocompleteWerteliste(
                        "anhang.abwasserart",
                        id + "-abwasserart",
                        "Abwasserart",
                        abwasserArtOptionen,
                        false,
                        disabled
                    )}
                    {formFactory.renderError("anhang", id + "-anhangError")}
                </div>
            </EAFieldset>
            <div className="gridl" style={{paddingTop: "var(--vertical-spacing)", gridTemplateColumns: "200px 200px 1fr"}}>
                {formFactory.renderSelect(
                    "zustaendigeBehoerde",
                    "messstelle-zustaendige-behoerde",
                    "Zuständige Behörde",
                    zustaendigkeitenOptionen,
                    disabled,
                    {
                        loading: zustaendigkeitenOptionenLoading,
                        optionTitle: "value",
                        optionKey: "id",
                    }
                )}
                {formFactory.renderSelect(
                    "limsBerechtigte",
                    "messstelle-lims-berechtiger",
                    "LIMS-Berechtigter",
                    zustaendigkeitenOptionen,
                    disabled,
                    {
                        loading: zustaendigkeitenOptionenLoading,
                        optionTitle: "value",
                        optionKey: "id",
                    }
                )}
                {formFactory.renderAutocompleteWithCustomChipLabelFunction(
                    "zugehoerigeBehoerden",
                    "zugehoerigeBehoerden",
                    "Zugehoerige Behoerden",
                    zustaendigkeitenOptionen,
                    (item) => item.value,
                    "value",
                    "id",
                    zustaendigkeitenOptionenLoading,
                    true,
                    zustaendigeBehoerdenDisabled,
                    {style: {gridColumnEnd: "span 1"}}
                )}
            </div>
        </div>
    );
}

export default MessstellenDaten;
